import React, { useState } from 'react';
import { X } from 'lucide-react';
import {
    updateCodigoOrdenConfig,
    CodigoOrdenConfig
} from '../../services/codigoOrdenService';

type EditOrdenPageProps = {
    config: CodigoOrdenConfig;
    onClose: () => void;
    onUpdated: (updatedConfig: CodigoOrdenConfig) => void;
};

const EditOrdenPage: React.FC<EditOrdenPageProps> = ({ config, onClose, onUpdated }) => {
    const [nombre, setNombre] = useState<string>(config.nombre);
    const [prefijo, setPrefijo] = useState<string>(config.prefijo);
    const [activo, setActivo] = useState<boolean>(config.activo);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const updatedConfig = await updateCodigoOrdenConfig(config.id, { nombre, prefijo, activo });
            onUpdated(updatedConfig); // Pasamos el objeto actualizado
            onClose();
        } catch (err) {
            console.error(err);
            setError('Error al actualizar el código de orden');
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />
            <div className="bg-white p-6 rounded-xl w-full max-w-md mx-4 border border-gray-100 shadow-xl relative">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">Editar Código de Orden</h3>
                        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        {/* Sección de Información Básica */}
                        <div className="border-b border-gray-200 pb-4">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Nombre
                                    </label>
                                    <input
                                        type="text"
                                        value={nombre}
                                        onChange={e => setNombre(e.target.value)}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Prefijo
                                    </label>
                                    <input
                                        type="text"
                                        value={prefijo}
                                        onChange={e => setPrefijo(e.target.value.toUpperCase())}
                                        required
                                        maxLength={10}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sección de Estado */}
                        <div className="pt-4">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div>
                                    <span className="block text-sm font-medium text-gray-700 mb-1">
                                        Estado
                                    </span>
                                    <p className="text-xs text-gray-500">
                                        {activo ? 'Visible y activo' : 'Oculto e inactivo'}
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={activo}
                                        onChange={e => setActivo(e.target.checked)}
                                    />
                                    <div
                                        className={`w-10 h-6 rounded-full transition-colors ${activo ? 'bg-[#c1348b]' : 'bg-gray-300'}`}
                                    >
                                        <div
                                            className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform ${activo ? 'translate-x-5' : 'translate-x-0'}`}
                                        ></div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Botones de Acción */}
                    <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors font-medium"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 text-sm bg-[#c1348b] hover:bg-[#8f1f64] text-white rounded-lg transition-colors font-medium flex items-center justify-center min-w-[120px]"
                        >
                            {submitting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    Actualizando...
                                </span>
                            ) : (
                                'Actualizar Cambios'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditOrdenPage;